/**
 * M3C-04 — the project wizard's field layer (DI-D62 c1): each field reads its
 * rule from the picked version's `node.project_create` → `ui.fields`
 * (workflows/v1 passes the map through verbatim; M3C-04 owns the entry
 * shape, M3C-06's editor writes it).
 *
 * THE VOCABULARY (as implemented — recorded in the M3C-04 story §10):
 *
 *   key    = a `CreateProjectRequest` property (projects/v1 2.26.0), or the
 *            same name without its `Id` suffix (`customerContact` ≡
 *            `customerContactId`). Unknown keys are ignored (a newer editor
 *            may know more fields than this release).
 *   value  = "required" | "optional" | "hidden" | "prefilled"
 *            ("prefilled-from-workflow" is the same rule), or an object
 *            `{ "rule": <one of those>, "value"?: string }` — `value` is
 *            what a prefilled field holds (a tenant id for a party).
 *            Anything else → the field keeps today's rule.
 *
 *   required  rendered, validated as required;
 *   optional  rendered, never blocks;
 *   hidden    NOT rendered, NOT validated, NOT sent;
 *   prefilled rendered read-only with "set by the workflow"; the value (when
 *             the entry carries one) is sent; without a value nothing is
 *             sent — the server writes the party the version fixes
 *             (DI-D62 c1) and the auto-grant fires (XT-D23).
 *
 * `ui.fields: null` (the default v1) = every field exactly as today
 * (`TODAY`). `projectName` is always required (the API's one required
 * field): a rule cannot hide it or make it optional.
 */

export type FieldRuleKind = "required" | "optional" | "hidden" | "prefilled";

export interface FieldRule {
  kind: FieldRuleKind;
  /** A prefilled field's value (a tenant id for a party). */
  value: string | null;
}

/** The canonical keys this release understands (CreateProjectRequest names). */
export const WIZARD_FIELDS = [
  "projectName",
  "offerReference",
  "reportLanguage",
  "startDate",
  "endDate",
  "aiModelIds",
  "customerTenantId",
  "customerContactId",
  "contractorTenantId",
  "contractorContactId",
  "reportingSubcontractorTenantId",
] as const;
export type WizardField = (typeof WIZARD_FIELDS)[number];

/** Today's rules (PM / CWIZ): name + customer + contractor required. */
const TODAY: Record<WizardField, FieldRuleKind> = {
  projectName: "required",
  offerReference: "optional",
  reportLanguage: "optional",
  startDate: "optional",
  endDate: "optional",
  aiModelIds: "optional",
  customerTenantId: "required",
  customerContactId: "required",
  contractorTenantId: "required",
  contractorContactId: "required",
  reportingSubcontractorTenantId: "optional",
};

export type FieldRules = Readonly<Record<WizardField, FieldRule>>;

function canonical(key: string): WizardField | null {
  if ((WIZARD_FIELDS as readonly string[]).includes(key))
    return key as WizardField;
  const withId = `${key}Id`;
  if ((WIZARD_FIELDS as readonly string[]).includes(withId))
    return withId as WizardField;
  if (key === "aiModels") return "aiModelIds";
  return null;
}

function kindOf(v: unknown): FieldRuleKind | null {
  if (v === "required" || v === "optional" || v === "hidden") return v;
  if (v === "prefilled" || v === "prefilled-from-workflow") return "prefilled";
  return null;
}

function ruleOfEntry(v: unknown): FieldRule | null {
  const k = kindOf(v);
  if (k) return { kind: k, value: null };
  if (v && typeof v === "object") {
    const o = v as Record<string, unknown>;
    const kk = kindOf(o.rule ?? o.mode);
    if (!kk) return null;
    const value = typeof o.value === "string" && o.value ? o.value : null;
    return { kind: kk, value };
  }
  return null;
}

/** The rules of a version's project_create node (`ui.fields`, verbatim). */
export function readFieldRules(
  fields: Record<string, unknown> | null | undefined,
): FieldRules {
  const out = {} as Record<WizardField, FieldRule>;
  for (const f of WIZARD_FIELDS) out[f] = { kind: TODAY[f], value: null };
  if (fields && typeof fields === "object") {
    for (const [k, v] of Object.entries(fields)) {
      const f = canonical(k);
      const r = ruleOfEntry(v);
      if (!f || !r) continue;
      out[f] = r;
    }
  }
  // the API's one required field is never hidden nor optional
  out.projectName = { kind: "required", value: null };
  return out;
}

/** Every field exactly as today (no version picked / `ui.fields: null`). */
export const TODAY_RULES: FieldRules = readFieldRules(null);

export const SET_BY_WORKFLOW = "set by the workflow";

export const isHidden = (r: FieldRule): boolean => r.kind === "hidden";
export const isPrefilled = (r: FieldRule): boolean => r.kind === "prefilled";
export const isRequired = (r: FieldRule): boolean => r.kind === "required";

/** A project party (customer / contractor) as the two tenant|contact rules read. */
export interface PartyRules {
  /** Not rendered, not validated, not sent. */
  hidden: boolean;
  /** The modes offered (tenant = share with a partner company, contact = address only). */
  modes: ("tenant" | "contact")[];
  required: boolean;
  /** The workflow fixes the party: shown read-only, `value` sent when known. */
  prefilled: { mode: "tenant" | "contact"; value: string | null } | null;
}

export function partyRules(
  rules: FieldRules,
  party: "customer" | "contractor",
): PartyRules {
  const t = rules[`${party}TenantId`];
  const c = rules[`${party}ContactId`];
  if (isPrefilled(t))
    return {
      hidden: false,
      modes: ["tenant"],
      required: false,
      prefilled: { mode: "tenant", value: t.value },
    };
  if (isPrefilled(c))
    return {
      hidden: false,
      modes: ["contact"],
      required: false,
      prefilled: { mode: "contact", value: c.value },
    };
  const modes: ("tenant" | "contact")[] = [];
  if (!isHidden(t)) modes.push("tenant");
  if (!isHidden(c)) modes.push("contact");
  const required =
    (!isHidden(t) && isRequired(t)) || (!isHidden(c) && isRequired(c));
  return { hidden: modes.length === 0, modes, required, prefilled: null };
}
